// A tiny authenticated GET helper shared by the People/Company/My Desk
// pages. Distinct from api.ts (which is auth/session-only, per its own
// header comment) — this is for page-level domain reads against the
// already-authenticated Worker endpoints those pages call directly (the
// roster fetch, a profile drill-in, Company, My Desk). It is deliberately
// NOT used for navigation between top-level pages (Home/Tasks/People/...) —
// that stays zero-network per INV-1 once the store/hydration layer lands.
import { authToken } from './auth';
import { API_BASE } from './config';
import { withViewAs } from './viewAs';

export class ApiError extends Error {
  status: number;

  /**
   * The refusal body the server actually sent, when there was one.
   *
   * `message` alone is not always the whole refusal: Manager Entry answers a
   * bad batch with `{ error: 'Validation failed.', problems: [...] }`, and the
   * problems ARE the message — "Validation failed." on its own tells a manager
   * nothing about which of their twelve rows is wrong. Carried here rather
   * than flattened into the string so a caller can render the list as a list.
   */
  body: Record<string, unknown>;

  constructor(message: string, status = 0, body: Record<string, unknown> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * A token that expired mid-session is the same event as one that expired
 * before the page loaded: the person is signed out and needs the button back.
 * Without this, a token dying while the tab was open turned the next click
 * into "unauthorized: token expired" printed under a form, forever — the app
 * knew it was signed out and never said so.
 */
async function handleUnauthorized(status: number): Promise<void> {
  if (status !== 401) return;
  // Only a token that was REJECTED ends a session. A 401 with no token stored
  // is an unauthenticated call — nothing to expire, and reacting to it would
  // wipe the store and reset the session of someone who never had one. Found
  // by session.test.ts, where an unauthenticated background request was
  // enough to knock the next test out of 'fault' and into 'signed-out'.
  if (!authToken.value) return;
  const { expireSession } = await import('./session');
  await expireSession();
}

/**
 * GET `${API_BASE}${path}` with the stored bearer token. Throws ApiError on any
 * non-2xx or network failure.
 *
 * The view-as lens is applied HERE, once, rather than at each call site —
 * `withViewAs` adds `?as=<code>` when an owner is viewing a single clinic and
 * leaves the path alone otherwise. Doing it per call site is how one gets
 * missed, and a missed one returns four clinics' data inside a one-clinic view.
 */
/** A read that has not even started answering after this long is treated as
 * a stuck connection, not a slow server (N-091: stuck connections sat 40-113 s
 * while every real read here answers in under 5 s). */
export const READ_TIMEOUT_MS = 30_000;

/** GET with one retry on a fresh request when the first one hangs or drops.
 * Reads only — a write is never repeated from here. */
export async function fetchRead(url: string, init: RequestInit): Promise<Response> {
  for (let attempt = 0; ; attempt += 1) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), READ_TIMEOUT_MS) : null;
    try {
      return await fetch(url, controller ? { ...init, signal: controller.signal } : init);
    } catch (err) {
      if (attempt >= 1) throw err;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
}

export async function authedGet<T>(path: string): Promise<T> {
  const token = authToken.value;
  let res: Response;
  try {
    res = await fetchRead(`${API_BASE}${withViewAs(path)}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
  } catch (err) {
    throw new ApiError(err instanceof Error ? err.message : 'network error');
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    await handleUnauthorized(res.status);
    throw new ApiError(
      (body as { error?: string }).error || `HTTP ${res.status}`,
      res.status,
      body as Record<string, unknown>,
    );
  }
  return body as T;
}

/** Same as authedGet, but for a file the server streams (NHP print). */
export async function authedGetBlob(path: string): Promise<{ blob: Blob; contentType: string }> {
  const token = authToken.value;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${withViewAs(path)}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
  } catch (err) {
    throw new ApiError(err instanceof Error ? err.message : 'network error');
  }
  if (!res.ok) {
    await handleUnauthorized(res.status);
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as { error?: string }).error || `HTTP ${res.status}`,
      res.status,
      body as Record<string, unknown>,
    );
  }
  const blob = await res.blob();
  return { blob, contentType: res.headers.get('content-type') || blob.type || 'application/octet-stream' };
}

/**
 * POST/DELETE against a write endpoint, with the bearer token and an
 * Idempotency-Key.
 *
 * The key is REQUIRED by every write endpoint (SPEC-001 §11) and is generated
 * here rather than by the caller, so a page cannot forget it and get a 400 —
 * and so a double-click sends the SAME key twice and the server replays the
 * first result instead of writing twice. That is the whole double-submit
 * defence: `crypto.randomUUID()` is called once per ACTION, not once per
 * attempt, which is why callers pass their own `key` when they retry.
 *
 * Writes are the one thing pages ARE allowed to call the network for — INV-1
 * budgets navigation at zero network calls, not user actions. Reads still go
 * through the store.
 *
 * The view-as lens rides on writes too, because the server reads `?as=` for
 * EVERY request, not only GETs (src/worker.js `contextFor` builds one context
 * off the URL before the route is even chosen). Matching that is the honest
 * behaviour: an owner acting inside a clinic's lens should be acting on that
 * clinic. It is not a demotion — `assertCanWriteLocation` (src/access.js)
 * still lets an OWNER write anywhere, lens or no lens, by design.
 */
export async function authedWrite<T>(
  // PATCH joined the list for the standing-list edit (2026-08-15). It is also
  // in the Worker's CORS allow-methods — a method missing there fails at
  // preflight, in the browser, where no server test can see it.
  // PUT joined for the My Desk layout save (N-090) — a whole-document
  // upsert, same CORS-methods requirement as PATCH above.
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  key: string = crypto.randomUUID(),
): Promise<T> {
  const token = authToken.value;
  const headers: Record<string, string> = { 'idempotency-key': key };
  if (token) headers.authorization = `Bearer ${token}`;
  if (body !== undefined) headers['content-type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${withViewAs(path)}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError(err instanceof Error ? err.message : 'network error');
  }
  const parsed = await res.json().catch(() => ({}));
  if (!res.ok) {
    // The server's own refusal text, surfaced verbatim: these endpoints name
    // the rule they refused on ("this period is closed", "not your clinic"),
    // and replacing that with "Something went wrong" is how a manager ends up
    // unable to tell a locked period from a broken app (INV-9).
    await handleUnauthorized(res.status);
    throw new ApiError(
      (parsed as { error?: string }).error || `HTTP ${res.status}`,
      res.status,
      parsed as Record<string, unknown>,
    );
  }
  return parsed as T;
}
